import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import {useNavigate, useParams, useSearchParams, useLocation} from 'react-router-dom';
import styled  from 'styled-components';
import { t } from 'ttag';
import useBasketContext from '../../../contexts/basket/useBasketContext';
import { TChannel } from '../../../contexts/operator/operator-context';
import useOperatorContext from '../../../contexts/operator/useOperatorContext';
import { Item } from '../../../contexts/site/site-context';
import useSiteContext from '../../../contexts/site/useSiteContext';
import PageSpinnerThree from '../../general/PageSpinnerThree';
import AppLayout from '../../layouts/app/AppLayout';
import ItemModal from './components/items/modal/ItemModal';
import MenuNav from './components/MenuNav';
import MenuCategory from './components/MenuCategory';
import MenuCategorySelector from './components/MenuCategorySelector';
import Banner from './components/Banner';
import OfferCards from './components/OfferCards';
import OrderModal from './components/order/OrderModal';
import UpsellModal from './components/order/UpsellModal';
import useScrollPosition from "../../../hooks/useScrollPosition";
import PreferencesModal from "./PreferencesModal";
import ChannelClosedWelcomeModal from "./ChannelClosedWelcomeModal";
import DeliveryInfoModal from "./DeliveryInfoModal";
import AllergyInfoModal from "./components/order/AllergyInfoModal";
import CartButton from "./components/order/CartButton";
import { CTA } from "../../general/Button";
import {iLog} from "../../../index";
import config from "../../../config/channels";
import useGroupContext from "../../../contexts/group/useGroupContext";
import { groupLockedMessage } from "../../../services/groupOrder";
import { toItems, track, valueOf } from '../../../services/analytics';
import GroupBanner from "./components/group/GroupBanner";
import GroupBasketPanel, { GuestBasketModal } from "./components/group/GroupBasket";
import GroupEndedModal from "./components/group/GroupEndedModal";
import GroupJoinModal from "./components/group/GroupJoinModal";
import GroupStartModal from "./components/group/GroupStartModal";

type MenuPageProps = {
    channel: TChannel;
    // the site to show, where it isn't in the URL
    restaurantId?: string;
    // shown behind the site select modal: a view of the menu that keeps out of the order's way
    preview?: boolean;
};

export type Preferences = {
    calories: boolean;
    glutenFree: boolean;
    vegan: boolean;
    vegetarian: boolean;
    halal: boolean;
    kosher: boolean;
    celery : boolean;
    'cereals-containing-gluten' : boolean;
    crustaceans : boolean;
    eggs : boolean;
    fish : boolean;
    lupin : boolean;
    milk : boolean;
    molluscs : boolean;
    mustard : boolean;
    peanuts : boolean;
    sesame : boolean;
    soybeans : boolean;
    'sulphur-dioxide-and-sulphites' : boolean;
    'tree-nuts': boolean
};

const MenuPage = ({ channel, restaurantId, preview = false }: MenuPageProps) => {
    const navigate = useNavigate();
    const location = useLocation();

    const scrollPosition = useScrollPosition();

    const params = useParams();
    const restaurant_id = restaurantId ?? params.restaurant_id;

    // need to get the table number out on dine-in
    const [searchParams] = useSearchParams();

    const [itemShow, setItemShow] = useState(false);
    const [editItem, setEditItem] = useState(false);
    const itemShowRef = useRef(itemShow);
    itemShowRef.current = itemShow;

    const [orderShow, setOrderShow] = useState(false);
    const orderShowRef = useRef(orderShow);
    orderShowRef.current = orderShow;

    const [upsellShow, setUpsellShow] = useState(false);
    const upsellShowRef = useRef(upsellShow);
    upsellShowRef.current = upsellShow;

    const [modalItem, setModalItem] = useState<null | Item>(null);

    // controls whether menu is view only or not
    const [viewOnly, setViewOnly] = useState(false);

    const operatorContext = useOperatorContext();
    const siteContext = useSiteContext();
    const basketContext = useBasketContext();
    const groupContext = useGroupContext();

    // the group order in the link we were sent
    const groupLinkToken = searchParams.get('group');
    const isGuest = groupContext.active && !groupContext.isLead;

    const [groupStartShow, setGroupStartShow] = useState(false);
    const [guestBasketShow, setGuestBasketShow] = useState(false);

    const [singleSiteAndChannel, setSingleSiteAndChannel] = useState(false);
    const [channelClosedWelcomeShow, setChannelClosedWelcomeShow] = useState(false);
    const [deliveryInfoShow, setDeliveryInfoShow] = useState(false);

    const [preferencesShow, setPreferencesShow] = useState(false);
    const [preferences, setPreferences] = useState<Preferences>({
        calories: true,
        glutenFree: true,
        vegan: true,
        vegetarian: true,
        halal: true,
        kosher: true,

        celery : false,
        'cereals-containing-gluten' : false,
        crustaceans : false,
        eggs : false,
        fish : false,
        lupin : false,
        milk : false,
        molluscs : false,
        mustard : false,
        peanuts : false,
        sesame : false,
        soybeans : false,
        'sulphur-dioxide-and-sulphites' : false,
        'tree-nuts': false
    });

    const [bannerHeight, setBannerHeight] = useState(0);
    const bannerObserverRef = useRef<ResizeObserver | null>(null);

    const [allergyInfoShow, setAllergyInfoShow] = useState(false);
    const [allergyInfoShown, setAllergyInfoShown] = useState(false);

    const [visibleUpsells, setVisibleUpsells] = useState<Item[]>([]);
    const [displayUpsell, setDisplayUpsell] = useState(false);
    const [orderShowTimeslotHelper, setOrderShowTimeslotHelper] = useState(false);
    const [orderUnavailableScheduledItems, setOrderUnavailableScheduledItems] = useState<string[]>([]);
    const [orderTimeslotError, setOrderTimeslotError] = useState<string|null>(null);

    useEffect(() => {
        // direct entry from say a QR code..
        if ( searchParams.has('table') ) {
            basketContext.setTableNumber(searchParams.get('table'));
            navigate(`/${config.dineIn.handle}/${restaurant_id}`);
        }
    }, []);

    useEffect(() => {
        window.addEventListener('popstate', handleBrowserBack);

        return () => {
            window.removeEventListener('popstate', handleBrowserBack);
        }
    }, []);

    const handleBrowserBack = (event: any) => {
        if (orderShowRef.current || upsellShowRef.current || itemShowRef.current) {
            setOrderShow(false);
            setUpsellShow(false);
            setItemShow(false);
            setModalItem(null);
        } else {
            navigate('/');
        }
    }

    useEffect(() => {
        siteContext.setChannel(channel);
    }, [channel]);

    useEffect(() => {

        if (
            siteContext &&
            operatorContext &&
            operatorContext.operator &&
            //!siteContext.loading &&
            !operatorContext.loading
        ) {

            let postcode = localStorage.getItem('postcode') ?? '';
            let table = localStorage.getItem('table') ?? '';

            if (table.length > 0) {
                basketContext.setTableNumber(table);
            }

            siteContext.fetchSite(restaurant_id ?? '',
                channel,
                undefined,
                undefined,
                basketContext.tableNumber ?? table ?? undefined,
                postcode,
                !!(groupLinkToken ?? groupContext.session)
            );

            // TODO: What happens when this fails?
        } else {
            iLog('Site context not fired.', [siteContext, operatorContext, restaurant_id]);
        }
    }, [restaurant_id, operatorContext]);

    useEffect(() => {

        if ( siteContext.site?.channels
            && Object.keys(siteContext.site?.channels).length === 1
            && siteContext.site?.sites_count === 1
        ) {
            setSingleSiteAndChannel(true);
        }

        if ( siteContext.site && channel !== 'catering' ) {

            let channelData = siteContext.site.channels[channel];

            if ( channelData ) {
                iLog('channel data', [channelData, !channelData?.is_open]);
                setViewOnly(!channelData?.is_open);
            }

            if ( searchParams.has('viewOnly') ) {
                setViewOnly(searchParams.get('viewOnly') === '1');
            }

            iLog('viewOnly', viewOnly);
            setChannelClosedWelcomeShow(viewOnly);

        }

    }, [ siteContext.site, viewOnly ]);

    useEffect(() => {
        if ( siteContext.site && !searchParams.has('viewOnly') && ( channel === 'delivery' || channel === 'catering' || channel === 'venues' ) ) {
            siteContext.fetchTimeslots(siteContext.site?.uid || '', siteContext.flow ?? 'noflow');

            // (guests are having someone else's order delivered, so there's nothing for them to read about it)
            setDeliveryInfoShow(!groupLinkToken && !(groupContext.session && !groupContext.session.isLead));
        }
    }, [ siteContext.site ]);

    useEffect(() => {
        setVisibleUpsells(siteContext.menu?.upsells.filter((item) => {
            let count = 0;
            basketContext.basketItems
                .filter((i) => item.uid === i.uid)
                .forEach((i) => count += (i.quantity ?? 0));
            return !item.is_hidden && count === 0;
        }) ?? []);
    }, [siteContext.menu, basketContext.basketItems]);

    useEffect(() => {
        if (operatorContext.operator?.theme.v4.upsell && visibleUpsells.length > 0) {
            setDisplayUpsell(true);
        } else {
            setDisplayUpsell(false);
        }
    }, [visibleUpsells]);

    // The lead is choosing a time or checking out while the basket's summary is open, and guests can't add anything.
    // Back on the menu, they can again.
    useEffect(() => {
        if ( !preview && groupContext.active && groupContext.isLead ) {
            groupContext.setLocked(orderShow || upsellShow);
        }
    }, [orderShow, upsellShow, groupContext.active]);

    // A group order is for one site's catering. Somewhere else, it's over.
    useEffect(() => {
        const session = groupContext.session;

        if ( !preview && session && siteContext.site && (session.siteUid !== siteContext.site.uid || channel !== config.catering.name) ) {
            if ( session.isLead ) groupContext.cancel(); else groupContext.leave();
            basketContext.clearBasket();
        }
    }, [siteContext.site, channel]);

    // Once the lead has placed the order, guests are told
    useEffect(() => {
        if ( groupContext.ended === 'placed' && groupContext.session && !groupContext.session.isLead ) {
            navigate('/group-confirmation');
        }
    }, [groupContext.ended]);

    const bannerRef = useCallback((node: HTMLDivElement | null) => {
    	if (typeof ResizeObserver === 'undefined') return;

        if (bannerObserverRef.current) {
        	bannerObserverRef.current.disconnect();
        	bannerObserverRef.current = null;
    	}

        if (!node) return;

        bannerObserverRef.current = new ResizeObserver(([entry]) => {
            setBannerHeight(entry.contentRect.height);
        });

        bannerObserverRef.current.observe(node);
    }, []);

    // a menu is looked at (not the one behind the site select modal, which is only there to be seen through)
    useEffect(() => {
        if (preview || !siteContext.site) return;

        track('menu_view', { channel, site_handle: siteContext.site.handle });
    }, [preview, siteContext.site?.uid]);

    // the basket is looked at
    useEffect(() => {
        if (!orderShow || preview) return;

        const lines = basketContext.basketItems ?? [];
        track('view_cart', { channel, site_handle: siteContext.site?.handle }, {
            ecommerce: { value: valueOf(lines), items: toItems(lines) },
        });
    }, [orderShow]);

    const showItemModal = (item: Item, edit: boolean) => {
        if (!edit && !preview) {
            track('view_item', { channel, site_handle: siteContext.site?.handle }, {
                ecommerce: { value: valueOf([item], 1), items: toItems([item], 1) },
            });
        }
        // setViewOnly(searchParams.get('viewOnly') === '1');

        iLog('showItemModal', [edit, item, basketContext.basketItems]);

        setModalItem(item);
        window.history.pushState({ isPopup: true }, 'Item');
        setItemShow(true);
        setEditItem(edit);
    };

    const updatePreferences = (preferences: Preferences) => {
        setPreferences(preferences);
    };

    if (!operatorContext || !operatorContext.operator || !siteContext) {
        return <PageSpinnerThree show={true} />;
    }

    // behind the site select modal, the menu there stays while the next one comes
    const keepMenu = preview && !!siteContext.menu && !operatorContext.loading;

    if ((operatorContext.loading || siteContext.loading) && !keepMenu) {
        // the layout holds the app's styles, which the site select modal over a preview still needs
        return preview ? (
            <AppLayout page="menu">
                <PageSpinnerThree show={true} />
            </AppLayout>
        ) : (
            <PageSpinnerThree show={true} />
        );
    }

    const styleVariant = operatorContext.operator?.theme?.v4.card_style ?? 'landscape';

    return (
        <AppLayout page="menu">

            <Helmet>
                { !preview && (
                    <title>
                        {t`Order`} {operatorContext.operator?.name ?? ''} {t`from`} {siteContext.site?.name ?? ''} -{' '}
                        {t`Powered by Orderswift`}
                    </title>
                )}
            </Helmet>
            <OrderModal
                reset={false}
                items={basketContext.basketItems}
                show={orderShow}
                onHide={() => setOrderShow(false)}
                showItemModal={showItemModal}
                displayUpsell={displayUpsell}
                onContinueToUpsell={() => { setOrderShow(false); setUpsellShow(true) }}
                injectShowTimeslotHelper={orderShowTimeslotHelper}
                injectTimeslotError={orderTimeslotError}
                injectUnavailableScheduledItems={orderUnavailableScheduledItems}
                preferences={preferences}
            />
            <UpsellModal
                upsells={visibleUpsells}
                show={upsellShow}
                onHide={() => setUpsellShow(false)}
                onReturnToOrder={(upsellShowTimeslotHelper, upsellTimeslotError, upsellUnavailableScheduledItems) => {
                    setUpsellShow(false);
                    setOrderShowTimeslotHelper(upsellShowTimeslotHelper);
                    setOrderTimeslotError(upsellTimeslotError);
                    setOrderUnavailableScheduledItems(upsellUnavailableScheduledItems);
                    setOrderShow(true);
                }}
                showItemModal={showItemModal}
                preferences={preferences}
            />
            <ChannelClosedWelcomeModal
                channel={channel}
                show={channelClosedWelcomeShow && !preview}
                singleSiteAndChannel={singleSiteAndChannel}
                onHide={() => setChannelClosedWelcomeShow(false)}
            />
            <DeliveryInfoModal
                channel={channel}
                show={deliveryInfoShow}
                onHide={() => setDeliveryInfoShow(false)}
            />
            <GroupJoinModal
                token={channel === config.catering.name ? groupLinkToken : null}
                onCancel={() => navigate('/')}
            />
            <GroupStartModal show={groupStartShow} onHide={() => setGroupStartShow(false)} />
            <GroupEndedModal />
            <GuestBasketModal
                show={guestBasketShow}
                onHide={() => setGuestBasketShow(false)}
                showItemModal={showItemModal}
            />
            <TopWrapper>
                <div className={'nav-top__logo'}>
                    <MenuNav
                        channel={channel}
                        singleSiteAndChannel={singleSiteAndChannel}
                        onStartGroup={
                            channel === config.catering.name && siteContext.site?.group_ordering && !groupContext.active
                                ? () => setGroupStartShow(true)
                                : undefined
                        }
                    />
                </div>
            </TopWrapper>
            <BottomWrapper className={`${scrollPosition > 0 && !siteContext.site?.banner ? 'scrolled-bottom-wrapper' : ''}`} bottomBorder={!siteContext.site?.banner}>
                <div>
                    <MenuCategorySelector bannerOffset={siteContext.site?.banner || groupContext.active ? true : false} bannerOffsetHeight={bannerHeight} categories={siteContext.menu?.categories} />
                    { operatorContext.operator?.theme.v4.preferences_modal && (
                        <div className={'fab__row'}>
                            <MenuFilterButton onClick={() => setPreferencesShow(true)}>
                                <span className="material-symbols-outlined">settings</span>
                            </MenuFilterButton>
                            <PreferencesModal
                                show={preferencesShow}
                                onHide={() => {
                                    setPreferencesShow(false);
                                }}
                                originalPreferences={preferences}
                                updatePreferences={updatePreferences}
                            />
                        </div>
                    )}
                </div>
            </BottomWrapper>
            <div ref={bannerRef}>
                <Banner text={siteContext.site?.banner} show={!!siteContext.site?.banner} />
                <GroupBanner />
            </div>
            <MenuWrap className="relative menu-wrap">
                <MenuContainer
                    variant={styleVariant}
                >
                    <MenuColumns>
                    <MenuContent>
                        <MenuList>
                            <ItemModal
                                show={itemShow}
                                onHide={() => {
                                    setItemShow(false);
                                    setModalItem(null);
                                    setEditItem(false);
                                }}
                                originalItem={modalItem}
                                viewOnly={viewOnly || (isGuest && groupContext.locked)}
                                viewOnlyMessage={!viewOnly && isGuest && groupContext.locked ? groupLockedMessage(groupContext.host) : undefined}
                                edit={editItem}
                                preferences={preferences}
                            />
                            <OfferCards />
                            <div className='menuWrapper'>
                                {siteContext.menu?.categories.filter((cat) => cat.name.toLowerCase() !== 'upsell').map((category, index) => (
                                    <MenuCategory
                                        key={category.uid + '-cat'}
                                        category={category}
                                        showItemModal={ showItemModal }
                                        menuItemStyle={operatorContext.operator?.theme?.v4.card_style ?? 'landscape'}
                                        isFirst={index === 0}
                                        preferences={preferences}
                                        bannerOffset={siteContext.site?.banner || groupContext.active ? true : false}
                                        bannerOffsetHeight={bannerHeight}
                                    />
                                ))}
                            </div>
                        </MenuList>
                    </MenuContent>
                    { groupContext.active && (
                        <GroupSidebar>
                            <GroupBasketPanel
                                showItemModal={showItemModal}
                                onCheckout={() => {
                                    window.history.pushState({ isPopup: true }, 'Order');
                                    setOrderShow(true);
                                }}
                            />
                        </GroupSidebar>
                    )}
                    </MenuColumns>
                    <FootNote>
                        <div>
                            <span className="material-symbols-outlined">info</span>
                            <span>Adults need around 2000 kcal a day</span>
                        </div>
                        <div>
                            <div>
                                <Badge className="badge--menu-item badge--menu-item--circular" title="Vegetarian">
                                    V
                                </Badge>
                                <span>Vegetarian</span>
                            </div>
                            <div>
                                <Badge className="badge--menu-item badge--menu-item--circular" title="Vegan">
                                    Vg
                                </Badge>
                                <span>Vegan</span>
                            </div>
                            <div>
                                <Badge className="badge--menu-item badge--menu-item--circular" title="Gluten-Free">
                                    Gf
                                </Badge>
                                <span>Gluten-Free</span>
                            </div>
                            <div>
                                <Badge className="badge--menu-item badge--menu-item--circular" title="Halal">
                                    H
                                </Badge>
                                <span>Halal</span>
                            </div>
                            <div>
                                <Badge className="badge--menu-item badge--menu-item--circular" title="Kosher">
                                    K
                                </Badge>
                                <span>Kosher</span>
                            </div>
                        </div>
                    </FootNote>
                </MenuContainer>
            </MenuWrap>

            <OrderButtonContainer className={groupContext.active ? 'has-group-sidebar' : ''}>
                <AllergyInfoModal
                    show={allergyInfoShow}
                    onHide={() => setAllergyInfoShow(false)}
                    onContinue={() => { setAllergyInfoShow(false); setOrderShow(true) }}
                />
                <CartButton
                    CTAProps={{ dual: true }}
                    onClick={() => {
                        // guests only have their own items, and there's nothing else for them to do
                        if ( isGuest ) {
                            setGuestBasketShow(true);
                            return;
                        }

                        window.history.pushState({ isPopup: true }, 'Order');
                        if ( ((siteContext.channel === config.dineIn.name && operatorContext.operator?.theme.v4.allergens_advice_onsite) || (siteContext.channel !== config.dineIn.name && operatorContext.operator?.theme.v4.allergens_advice)) && !allergyInfoShown ) {
                            setAllergyInfoShown(true);
                            setAllergyInfoShow(true);
                            return;
                        } else {
                            setOrderShow(true);
                        }
                    }}
                />
            </OrderButtonContainer>

        </AppLayout>
    );
};

const FootNote = styled.div`
    padding: 0 16px;
    font-size: 12px;
    line-height: 16px;
    color: var(--text-grey);

    .material-symbols-outlined {
        margin-right: 8px;
        vertical-align: bottom;
    }

    @media screen and (min-width: 768px) {
        padding: 0 48px;
    }

    > div {
        margin-bottom: 12px;

        > div {
            display: inline-block;
            margin-bottom: 0;
            margin-right: 8px;

            span:first-child {
                width: 16px;
                height: 16px;
                font-size: 12px;
                line-height: 16px;
            }
        }
    }
`;

const MenuFilterButton = styled(CTA)`
    pointer-events: auto;
    width: 36px;
    height: 36px;
    padding: 0;
    background: #FFFFFF;
    color: var(--color_ui_5);
    font-size: 24px;
    line-height: 24px;

    &:hover, &:active {
        background: var(--bg-grey);
    }
`;

const TopWrapper = styled.div`
    position: relative;
    background-color: var(--layouts_header_bg);
    z-index: 11;
    padding: 0;
    display: flex;
    flex-direction: column;
    padding: 8px 0;
    .nav-top__logo {
        max-width: 1296px;
        padding: 0 48px;
        margin: 0 auto;
        width: 100%;
        @media screen and (max-width: 767px) { padding: 0 16px; }
    }

    @media screen and (min-width: 768px) {
        height: 60px;
        align-items: center;
        flex-direction: row;
    }
`;

const MiddleWrapper = styled.div`
    @media screen and (min-width: 768px) {
        display: none;
    }
`;

const BottomWrapper = styled.div<{ bottomBorder: boolean }>`
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: var(--layouts_nav_bg);
    max-width: 100%;

    ${props => props.bottomBorder ? `
        border-bottom: var(--layouts_nav_border);
        box-shadow: 0 1px 4px rgb(0 0 0 / 8%);
    ` : ''}

    > div {
        max-width: 1296px;
        padding: 0;
        margin: 0 auto;
        display: flex;
        gap: 16px;
        align-items: center;
        flex-wrap: nowrap;

        @media screen and (min-width: 768px) {
            padding: 0 48px;
        }
    }
`;

const MenuList = styled.div`
    width: 100%;
    overflow: auto;
    overflow-x: hidden;
    position: relative;
    .menu-list-shadow {
        position: absolute;
        top: 50px;
        height: 16px;
        background-color: #FFFFFF;
        left: 0;
        right: 0;
        z-index: 20;
        @media screen and (min-width: 768px) {
            display: none;
        }
    }
    .menu-list-alert {
        padding: 0 1rem;
        margin-bottom: 1.5rem;
        margin-top: 1rem;
        display: none;
        @media screen and (max-width: 767px) {
            display: none;
        }
    }
    @media screen and (min-width: 768px) {
        position: static;
    }

    @media screen and (min-width: 1140px) {
        padding-top: 0;
        margin-top: 0;
    }

    .menuWrapper {
        padding-top: 24px;
    }
`;

const OrderButtonContainer = styled.div`
    display: flex;
    position: fixed;
    z-index: 999;
    padding: 16px;
    background: var(--layouts_footer_bg);
    margin: 0;
    left: 0;
    bottom: 0;
    right: 0;
    border-top: var(--layouts_footer_border);
    box-shadow: 0 -1px 4px rgb(0 0 0 / 8%);
    justify-content: center;

    &.has-group-sidebar {
        @media screen and (min-width: 1140px) {
            display: none;
        }
    }

    button {
        width: 100%;
        max-width: 420px;
        height: 48px;
        padding: 0 16px;
    }
`;

const MenuColumns = styled.div`
    @media screen and (min-width: 1140px) {
        display: flex;
        align-items: flex-start;
        gap: 32px;
    }
`;

// Beside the menu where there's room for it, in place of the cart button
const GroupSidebar = styled.aside`
    display: none;

    @media screen and (min-width: 1140px) {
        display: block;
        flex: 0 0 360px;
        position: sticky;
        top: 88px;
        margin: 24px 48px 0 0;
    }
`;

const MenuContent = styled.div`
    min-width: 0;
    flex: 1;
    @media screen and (min-width: 1140px) {
        display: flex;
    }
`;

const MenuWrap = styled.div`
    background-attachment: fixed;
`;

const MenuContainer = styled.div<{ variant?: string }>`
    max-width: 1296px;
    margin: 0 auto;
    display: block;
    flex: 1;
    padding-bottom: 150px;
    position: relative;
    @media screen and (min-width: 768px) {
        display: flex;
        flex-direction: column;
    }

    .locationClosed__alert {
        position: fixed;
        left: 0;
        bottom: 0;
        background: #2E3333;
        max-width: 100vw;
        width: 100vw;
        color: white;
        padding: 18px 48px;
        display: flex;
        align-items: center;

        img { margin-right: 8px; }

        a {
            margin-left: 4px;
            color: white;
        }
    }
`;

export default MenuPage;
